import { useId, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { Contact, Paginated } from '@kontor/shared';
import { api, queryString } from '../api';
import './person-picker/person-picker.css';

export interface PickedContact {
  id: string;
  name: string;
}

/**
 * Einen Kontakt über ein Suchfeld wählen (Name, E-Mail, Telefon oder Ort), mit Vorschlägen vom Server. Genutzt für den Empfänger eines Angebots und den
 * Lieferanten eines Produkts. Der gewählte Kontakt steht als Marke da und lässt sich wieder entfernen.
 */
export function ContactPicker({ value, onChange, invalid, label, hint }: { value: PickedContact | null; onChange: (c: PickedContact | null) => void; invalid?: boolean; label: string; hint?: ReactNode }) {
  const uid = useId();
  const [text, setText] = useState('');
  const [open, setOpen] = useState(false);
  const found = useQuery({
    queryKey: ['contacts', { pick: text }],
    queryFn: async () => (await api<Paginated<Contact>>('GET', `/contacts${queryString({ q: text.trim(), perPage: 8, sort: 'name' })}`)).data,
    enabled: open && text.trim().length >= 2,
  });
  if (value) {
    return (
      <div className="picker__chips">
        <span className="picker__chip">
          <span>{value.name}</span>
          <button type="button" className="picker__remove" onClick={() => onChange(null)} aria-label={`${label} entfernen`}>×</button>
        </span>
      </div>
    );
  }
  return (
    <div className="picker__box">
      <input
        className="input"
        role="combobox"
        aria-label={label}
        aria-expanded={open && !!found.data}
        aria-controls={`${uid}-list`}
        aria-invalid={invalid}
        value={text}
        placeholder="Kontakt suchen …"
        onChange={(e) => {
          setText(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
      />
      {open && found.data && (
        <ul id={`${uid}-list`} className="picker__list" role="listbox">
          {found.data.length === 0 && <li className="picker__empty">Kein Kontakt gefunden</li>}
          {found.data.map((c) => (
            <li
              key={c.id}
              role="option"
              aria-selected={false}
              className="picker__option"
              onMouseDown={(e) => {
                e.preventDefault();
                onChange({ id: c.id, name: c.name });
                setText('');
              }}
            >
              {c.name}
              <span className="muted small"> {[c.email, c.city].filter(Boolean).join(' · ')}</span>
            </li>
          ))}
        </ul>
      )}
      {hint && <small className="muted">{hint}</small>}
    </div>
  );
}
